/** Private valuation ranges. These do not change the marketplace's public scales. */
const band = (option, label, lower, upper) => ({ option, label, lower, upper });
export const UNKNOWN_ANSWER = { option: 'unknown', messageKey: 'unknown' };
export const NOT_APPLICABLE_ANSWER = { option: 'notApplicable', messageKey: 'notApplicable' };
const zero = band('zero', '0 €', 0, 0);
const amounts = (prefix, limits) => [
  zero,
  ...limits.map((upper, index) => {
    const lower = index === 0 ? 0 : limits[index - 1];
    const format = n => new Intl.NumberFormat('es-ES').format(n);
    return band(
      `${prefix}-${lower}-${upper}`,
      lower === 0 ? `Hasta ${format(upper)} €` : `${format(lower)}–${format(upper)} €`,
      lower,
      upper
    );
  }),
  band(
    `${prefix}-open`,
    `Más de ${new Intl.NumberFormat('es-ES').format(limits[limits.length - 1])} €`,
    limits[limits.length - 1],
    null
  ),
];
const annual = amounts('annual', [5000, 15000, 30000, 60000, 150000, 500000]);
const balance = amounts('balance', [10000, 50000, 100000, 250000, 500000, 1000000, 5000000]);
const profit = [
  band('loss-open', 'Pierde más de 100.000 €', null, -100000),
  band('loss-large', 'Pierde 25.000–100.000 €', -100000, -25000),
  band('loss-small', 'Pierde hasta 25.000 €', -25000, 0),
  ...amounts('profit', [25000, 75000, 150000, 500000, 1000000]),
];
const withUnknown = rows => [...rows, UNKNOWN_ANSWER];

/** Every former numeric field has explicit choices, including an unknown state. */
export const VALORADOR_FINANCIAL_BANDS = {
  profit: withUnknown(profit),
  salary: withUnknown(amounts('salary', [15000, 30000, 60000, 100000, 200000])),
  personal: withUnknown(annual),
  extraIncome: withUnknown(annual),
  extraExpense: withUnknown(annual),
  rent: withUnknown(annual),
  debt: withUnknown(balance),
  cash: withUnknown(balance),
  property: withUnknown(
    amounts('property', [100000, 250000, 500000, 1000000, 2500000, 5000000]).slice(1)
  ),
  stock: [...withUnknown(balance), NOT_APPLICABLE_ANSWER],
};

/** Preserve old amounts as selected bands; never coerce blank/unknown answers to zero. */
export const migrateValoradorFinancialAnswers = answers => {
  const next = { ...answers };
  Object.entries(VALORADOR_FINANCIAL_BANDS).forEach(([key, rows]) => {
    const value = next[key];
    if (value == null || value === '' || rows.some(row => row.option === value)) return;
    const numeric = typeof value === 'number' || typeof value === 'string' ? Number(value) : NaN;
    const row = Number.isFinite(numeric)
      ? rows.find(row =>
          row.option === 'zero'
            ? numeric === 0
            : row.lower !== undefined &&
              numeric !== 0 &&
              (row.lower == null || numeric >= row.lower) &&
              (row.upper == null || numeric < row.upper)
        )
      : null;
    next[key] = row?.option || 'unknown';
  });
  return next;
};

/** Unknown and inapplicable values remain null with distinct statuses for the future engine. */
export const valoradorFinancialEstimates = answers => {
  const result = {};
  Object.entries(VALORADOR_FINANCIAL_BANDS).forEach(([key, rows]) => {
    let selected = answers[key];
    if (key === 'rent' && ['si', 'none'].includes(answers.rentPaid)) selected = 'notApplicable';
    if (key === 'rent' && answers.rentPaid === 'unknown') selected = 'unknown';
    if (key === 'property' && answers.propertyIncluded === 'no') selected = 'notApplicable';
    if (key === 'property' && answers.propertyIncluded === 'unknown') selected = 'unknown';
    const row = rows.find(row => row.option === selected && row.lower !== undefined);
    result[key] = row
      ? {
          status: 'estimated',
          value:
            row.lower == null
              ? row.upper * 1.25
              : row.upper == null
              ? row.lower * 1.25
              : (row.lower + row.upper) / 2,
        }
      : {
          status: ['unknown', 'notApplicable'].includes(selected) ? selected : 'missing',
          value: null,
        };
  });
  return result;
};
